import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, Toolbar, IconButton, Typography, Menu, Container, Avatar, Button, MenuItem } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import BaseLayout from './BaseLayout';
import { env } from '../config/env';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { authApi } from '../services/authApi';
import ThemeToggle from '../components/ThemeToggle';
import { useColorScheme } from '@mui/material';

// Navigation links
const pages = ['Home', 'Dashboard', 'About'];
const routes = ['/', '/dashboard', '/about'];

const PublicLayout = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const { mode, setMode } = useColorScheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  // Navigation menu state
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleLogout = async () => {
    try {
      // Call the logout API endpoint
      await authApi.endpoints.logout.initiate().unwrap();
      // The logout action is already dispatched in the authApi onQueryStarted
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if API call fails, clear local state and redirect
      dispatch(logout());
      navigate("/");
    }
    handleCloseUserMenu();
  };

  return (
    <BaseLayout>
      <AppBar position="static" sx={{ backgroundColor: 'inherit', color: mode === 'light' ? 'text.primary' : 'white' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo for desktop */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mx: 2,
                display: { xs: 'none', md: 'flex' },
                color: mode === 'light' ? 'text.primary' : 'white'
              }}
            >
              <Box component="img" src={`assets/fulllogo${mode === 'light' ? '' : '-dark'}.webp`} alt="Logo" width="auto" height={60} />
            </Box>

            {/* Mobile menu icon and dropdown */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="navigation menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color={mode === 'light' ? 'text.primary' : 'white'}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {pages.map((page, index) => (
                  <MenuItem 
                    key={page} 
                    onClick={handleCloseNavMenu}
                    component={RouterLink}
                    to={routes[index]}
                  >
                    <Typography textAlign="center">{page}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            
            {/* Logo for mobile */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                color: mode === 'light' ? 'text.primary' : 'white',
              }}
            >
              <Box component="img" src={`assets/fulllogo${mode === 'light' ? '' : '-dark'}.webp`} alt="Logo" width="auto" height={60} />
            </Box>
            
            {/* Desktop navigation links */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {pages.map((page, index) => (
                <Button
                  key={page}
                  component={RouterLink}
                  to={routes[index]}
                  onClick={handleCloseNavMenu}
                  sx={{ my: 2, color: mode === 'light' ? 'text.primary' : 'white', display: 'block' }}
                >
                  {page}
                </Button>
              ))}
            </Box>

            {/* User menu section */}
            <Box sx={{ flexGrow: 0 }}>
              {isAuthenticated ? (
                <>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      {user?.username?.charAt(0).toUpperCase() || 'U'}
                    </Avatar>
                  </IconButton>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar-user"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <MenuItem onClick={handleLogout}>
                      <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/login"
                >
                  Login
                </Button>
              )}
            </Box>

            <ThemeToggle />
          </Toolbar>
        </Container>
      </AppBar>
      
      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          p: 2
        }}
      >
        {children || <Outlet />}
      </Box>
      
      {/* Footer */}
      <Box component="footer" sx={{ py: 3, bgcolor: 'background.paper', textAlign: 'center', width: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} {env.appTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Environment: {env.appEnv}
        </Typography>
      </Box>
    </BaseLayout>
  );
};

PublicLayout.propTypes = {
  children: PropTypes.node,
};

export default PublicLayout;
